import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import BrandLogo from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useQuestionnaire } from "@/context/QuestionnaireContext";

const PRIVACY_VERSION_LABEL = "1.0 – Agosto 2026";

const Sec = ({ n, title, children }) => (
  <div className="mt-5 first:mt-0">
    <h3 className="font-heading text-sm font-600 text-foreground leading-snug">{n}. {title}</h3>
    <div className="mt-1.5 text-sm text-muted-foreground leading-relaxed space-y-2">{children}</div>
  </div>
);

export default function Privacy() {
  const navigate = useNavigate();
  const { consents, setConsents, ensureSubmission, submissionId, resume, recordConsents } = useQuestionnaire();
  const [privacy, setPrivacy] = useState(!!consents.privacy_accepted);
  const [consent, setConsent] = useState(!!consents.consent_accepted);
  const [saving, setSaving] = useState(false);

  const canContinue = privacy && consent;

  const handleContinue = async () => {
    if (!canContinue || saving) return;
    const c = { privacy_accepted: privacy, consent_accepted: consent };
    setConsents(c);
    setSaving(true);
    try {
      if (submissionId && resume) {
        await recordConsents(submissionId, c);
      } else {
        await ensureSubmission({}, c);
      }
      navigate("/test");
    } catch (e) {
      toast.error("Impossibile avviare il test. Riprova.");
      setSaving(false);
    }
  };

  return (
    <div className="min-h-[100dvh] flex flex-col bg-background">
      <header className="w-full border-b border-border/60">
        <div className="mx-auto max-w-lg px-6 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="text-muted-foreground hover:text-foreground transition-colors" data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <BrandLogo variant="dark" className="h-7" />
          <span className="w-5" />
        </div>
      </header>

      <main className="flex-1 mx-auto w-full max-w-lg px-6 py-8 flex flex-col">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="fs-eyebrow">Prima di iniziare</p>
          <h1 className="mt-3 font-heading text-3xl font-700 tracking-tight">Informativa e consenso</h1>
          <p className="mt-3 text-base text-muted-foreground leading-relaxed">
            Prima di iniziare, ti chiediamo di prendere visione dell'informativa e di fornire i
            consensi richiesti.
          </p>

          {/* ---------- INFORMATIVA PRIVACY (ufficiale) ---------- */}
          <section className="mt-6 fs-card p-5" data-testid="informativa-privacy">
            <h2 className="font-heading text-lg font-600">Informativa Privacy</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Informativa sul trattamento dei dati personali ai sensi dell'art. 13 del Regolamento
              (UE) 2016/679 – GDPR
            </p>

            <div className="mt-4">
              <Sec n="1" title="Titolare del trattamento">
                <p>Il Titolare del trattamento è:</p>
                <p className="text-foreground">
                  FORMAeSALUTE Longevity Suite di Caprioli Antonio<br />
                  Via G. Murat 35<br />
                  85024 Lavello (PZ) – Italia<br />
                  P. IVA 02143880769<br />
                  Email: studioformaesalute@gmail.com
                </p>
              </Sec>

              <Sec n="2" title="Dati personali trattati">
                <p>
                  Attraverso il FORMAeSALUTE Longevity Check® e il relativo Longevity Test™ possono
                  essere raccolti dati personali quali dati identificativi, data di nascita,
                  professione, altezza, peso e informazioni relative ad attività fisica,
                  alimentazione, sonno e recupero, stress, stile di vita, abitudini e obiettivi
                  personali.
                </p>
                <p>
                  Il Longevity Test™ può inoltre comportare il trattamento di dati relativi alla
                  salute, quali informazioni riguardanti farmaci assunti, patologie diagnosticate,
                  interventi chirurgici, dolori, limitazioni e infortuni.
                </p>
                <p>
                  Tali informazioni costituiscono categorie particolari di dati personali ai sensi
                  dell'art. 9 del Regolamento (UE) 2016/679.
                </p>
              </Sec>

              <Sec n="3" title="Finalità del trattamento">
                <p>
                  I dati personali raccolti sono trattati esclusivamente per finalità connesse al
                  FORMAeSALUTE Longevity Check®, e in particolare per:
                </p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>effettuare la valutazione iniziale;</li>
                  <li>conoscere lo stato generale, le abitudini, lo stile di vita, le esigenze e gli obiettivi della persona;</li>
                  <li>disporre delle informazioni necessarie allo svolgimento del Longevity Check®;</li>
                  <li>supportare la definizione e la personalizzazione del percorso più appropriato nell'ambito dei servizi offerti da FORMAeSALUTE;</li>
                  <li>gestire e conservare la relativa scheda Longevity Test™.</li>
                </ul>
                <p>
                  Le informazioni raccolte attraverso il Longevity Test™ non costituiscono diagnosi
                  medica e non sostituiscono visite, diagnosi, trattamenti o prestazioni effettuate
                  da professionisti sanitari abilitati.
                </p>
              </Sec>

              <Sec n="4" title="Base giuridica del trattamento">
                <p>
                  Il trattamento dei dati personali ordinari necessari alla richiesta e allo
                  svolgimento del Longevity Check® è effettuato nell'ambito dell'esecuzione del
                  servizio richiesto dall'interessato e delle eventuali misure precontrattuali
                  connesse, ai sensi dell'art. 6, par. 1, lett. b) del GDPR.
                </p>
                <p>
                  Il trattamento dei dati relativi alla salute contenuti nel Longevity Test™ avviene
                  esclusivamente previo consenso esplicito dell'interessato, ai sensi dell'art. 9,
                  par. 2, lett. a) del GDPR.
                </p>
                <p>
                  Il consenso può essere revocato in qualsiasi momento, senza pregiudicare la
                  liceità del trattamento effettuato prima della revoca.
                </p>
              </Sec>

              <Sec n="5" title="Natura del conferimento">
                <p>La compilazione del Longevity Test™ è facoltativa.</p>
                <p>L'interessato è libero di scegliere se effettuare o meno il FORMAeSALUTE Longevity Check®.</p>
                <p>
                  Il mancato conferimento dei dati necessari o il mancato consenso al trattamento
                  dei dati relativi alla salute impedisce l'utilizzo del Longevity Test™ e lo
                  svolgimento del Longevity Check® basato sulle informazioni in esso contenute,
                  senza pregiudicare la possibilità di richiedere informazioni o usufruire, ove
                  possibile, degli altri servizi di FORMAeSALUTE.
                </p>
              </Sec>

              <Sec n="6" title="Modalità del trattamento e sicurezza">
                <p>
                  I dati personali sono trattati prevalentemente mediante strumenti informatici e
                  secondo principi di liceità, correttezza, trasparenza, minimizzazione e
                  limitazione delle finalità.
                </p>
                <p>
                  Sono adottate misure tecniche e organizzative adeguate a proteggere i dati da
                  accessi non autorizzati, perdita, distruzione, divulgazione, modifica o utilizzo
                  illecito.
                </p>
                <p>
                  L'accesso alle informazioni raccolte attraverso il Longevity Test™ è limitato ai
                  soggetti autorizzati che necessitano di accedervi per le finalità indicate nella
                  presente informativa.
                </p>
              </Sec>

              <Sec n="7" title="Elaborazione mediante sistemi di intelligenza artificiale">
                <p>
                  Alcune informazioni fornite nell'ambito del FORMAeSALUTE Longevity Check®,
                  comprese, quando presenti, le informazioni relative alla salute, possono essere
                  elaborate mediante sistemi di intelligenza artificiale utilizzati come strumento
                  di supporto alla predisposizione del report.
                </p>
                <p>
                  L'output generato costituisce esclusivamente un supporto allo staff di
                  FORMAeSALUTE: il report può essere verificato, integrato, modificato e approvato
                  dallo staff prima della consegna all'interessato.
                </p>
                <p>
                  Tale elaborazione non costituisce diagnosi medica e non configura un processo
                  decisionale automatizzato che produca effetti giuridici o incida in modo analogo
                  sull'interessato.
                </p>
              </Sec>

              <Sec n="8" title="Destinatari e fornitori">
                <p>
                  I dati personali potranno essere trattati, nei limiti strettamente necessari, da
                  fornitori di servizi tecnici, informatici, infrastrutturali, hosting e gestione
                  della piattaforma utilizzata per il Longevity Check®.
                </p>
                <p>
                  Tali soggetti saranno trattati e disciplinati secondo quanto previsto dalla
                  normativa applicabile e, quando ne ricorrano i presupposti, quali responsabili del
                  trattamento ai sensi dell'art. 28 del GDPR.
                </p>
                <p>I dati personali non saranno diffusi.</p>
              </Sec>

              <Sec n="9" title="Eventuali trasferimenti internazionali">
                <p>
                  Qualora, per esigenze tecniche connesse ai servizi informatici utilizzati, alcuni
                  dati personali siano trattati in Paesi esterni allo Spazio Economico Europeo, il
                  trattamento avverrà nel rispetto delle condizioni e delle garanzie previste dal
                  GDPR per i trasferimenti internazionali di dati personali.
                </p>
              </Sec>

              <Sec n="10" title="Periodo di conservazione">
                <p>
                  I dati raccolti attraverso il FORMAeSALUTE Longevity Check® saranno conservati per
                  un periodo massimo di 24 mesi dall'ultima interazione relativa al Longevity Check®,
                  salvo richiesta anticipata di cancellazione dell'interessato, revoca del consenso
                  ove applicabile o necessità di conservazione ulteriore prevista dalla legge.
                </p>
                <p>
                  Alla scadenza del periodo di conservazione, i dati saranno cancellati o resi
                  anonimi, salvo l'esistenza di una diversa base giuridica che ne consenta o imponga
                  l'ulteriore conservazione.
                </p>
              </Sec>

              <Sec n="11" title="Diritti dell'interessato">
                <p>
                  Nei casi e nei limiti previsti dal GDPR, l'interessato può esercitare i propri
                  diritti, tra cui:
                </p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>ottenere conferma dell'esistenza o meno di dati personali che lo riguardano;</li>
                  <li>accedere ai propri dati personali;</li>
                  <li>richiederne la rettifica o l'aggiornamento;</li>
                  <li>richiederne la cancellazione;</li>
                  <li>richiedere la limitazione del trattamento;</li>
                  <li>ottenere, ove applicabile, la portabilità dei dati;</li>
                  <li>opporsi al trattamento nei casi previsti dalla normativa;</li>
                  <li>revocare in qualsiasi momento il consenso prestato, senza pregiudicare la liceità del trattamento effettuato prima della revoca;</li>
                  <li>proporre reclamo all'Autorità Garante per la protezione dei dati personali.</li>
                </ul>
                <p>
                  Per esercitare i propri diritti o richiedere informazioni sul trattamento dei dati
                  è possibile contattare: studioformaesalute@gmail.com
                </p>
              </Sec>

              <Sec n="12" title="Aggiornamenti dell'informativa">
                <p>
                  La presente informativa potrà essere aggiornata qualora cambino le modalità o le
                  finalità del trattamento oppure per adeguamenti normativi o tecnici.
                </p>
                <p>
                  La versione applicabile è quella resa disponibile all'interessato al momento della
                  compilazione del Longevity Test™.
                </p>
              </Sec>

              <p className="mt-5 pt-4 border-t border-border/70 text-xs text-muted-foreground">
                Versione Informativa Privacy: {PRIVACY_VERSION_LABEL}
              </p>
            </div>
          </section>

          {/* ---------- CONSENSI ---------- */}
          <div className="mt-6 flex flex-col gap-3">
            <div
              role="button"
              tabIndex={0}
              onClick={() => setPrivacy((v) => !v)}
              onKeyDown={(e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); setPrivacy((v) => !v); } }}
              className="fs-card p-4 flex items-start gap-3.5 cursor-pointer fs-focus"
              data-testid="privacy-check-row"
            >
              <Checkbox checked={privacy} data-testid="privacy-checkbox" className="mt-0.5 h-5 w-5 pointer-events-none" />
              <span className="text-sm leading-relaxed">
                Dichiaro di aver letto e compreso l'<b>Informativa Privacy</b> relativa al
                FORMAeSALUTE Longevity Check®.
              </span>
            </div>

            <div className="fs-card p-4 bg-secondary/40">
              <p className="text-sm text-muted-foreground leading-relaxed">
                Il Longevity Test™ contiene domande che possono riguardare il tuo stato di salute,
                tra cui informazioni relative a farmaci, patologie, interventi chirurgici, dolori,
                limitazioni e infortuni.
              </p>
            </div>

            <div
              role="button"
              tabIndex={0}
              onClick={() => setConsent((v) => !v)}
              onKeyDown={(e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); setConsent((v) => !v); } }}
              className="fs-card p-4 flex items-start gap-3.5 cursor-pointer fs-focus"
              data-testid="consent-check-row"
            >
              <Checkbox checked={consent} data-testid="consent-checkbox" className="mt-0.5 h-5 w-5 pointer-events-none" />
              <span className="text-sm leading-relaxed">
                Acconsento espressamente al <b>trattamento dei miei dati relativi alla salute</b> per
                le finalità descritte nell'Informativa Privacy e connesse allo svolgimento del
                FORMAeSALUTE Longevity Check® e alla definizione del mio percorso personalizzato.
              </span>
            </div>

            <p className="px-1 text-xs text-muted-foreground">Versione consenso: {PRIVACY_VERSION_LABEL}</p>
          </div>
        </motion.div>

        <div className="flex-1 min-h-6" />

        <div className="mt-8 sticky bottom-6">
          <Button
            data-testid="privacy-continue-btn"
            disabled={!canContinue || saving}
            onClick={handleContinue}
            className="w-full h-14 rounded-full text-base font-semibold group disabled:opacity-40"
          >
            {saving ? "Avvio…" : "Continua"}
            {!saving && <ArrowRight className="ml-2 h-5 w-5 transition-transform duration-200 group-hover:translate-x-0.5" />}
          </Button>
        </div>
      </main>
    </div>
  );
}
